import { createFileRoute } from "@tanstack/react-router";
import { PageChrome } from "../components/PageChrome";
import { canonical, documentHeaders, faqJsonLd } from "../lib/seo";

/**
 * The App Store listing's Support URL, which is a required field and is checked.
 *
 * It existed nowhere before submission was attempted: `/privacy`, `/terms` and
 * `/delete` were all live and `/support` was a 404, which is a rejection rather
 * than a missing nicety. Apple wants a page a real person can reach us from, so
 * the address is the whole point of it — the rest is answering the questions
 * that would otherwise arrive as one-star reviews.
 *
 * `support@fazly.app` is the address already referenced elsewhere in the
 * project; this page is the first thing to publish it.
 */
const SUPPORT_EMAIL = "support@fazly.app";

/**
 * The questions this page already shows, as data for the FAQPage schema.
 *
 * Every one of these is rendered below in the same words. That is not a
 * formality — marking up an answer a visitor cannot see is the specific thing
 * Google issues manual actions for, and it is also how AI engines end up
 * quoting a page for something it does not say.
 *
 * ─── v2 REBUILD ─────────────────────────────────────────────────────────────
 * These five answered a different app: Screen Time permission, app locks, an
 * emergency unlock, and reporting a prayer request from a long press. Fazly has
 * none of those — it takes photographs and returns scores — so a visitor
 * arriving from the App Store listing's Support URL was being told to check a
 * settings screen that does not exist. The photo question is first now because
 * it is the one somebody writes in about, and the reporting answer matches
 * Terms §5, which routes reports to this address.
 */
const SUPPORT_FAQ = [
  {
    q: "What happens to my photos",
    a: "They are used for the analysis and deleted within seven days. They are sent only to the provider that scores them, they are never shown to another user, and they are never used to train anything. Deleting your profile removes them straight away.",
  },
  {
    q: "My scan failed or the result looks wrong",
    a: "Take it again in even, front-on light with your hair off your face and nothing covering your jaw. A score is an estimate from a handful of photographs, so two scans in different light can disagree — if one looks badly wrong, retake it and write to us if it happens again.",
  },
  {
    q: "Managing or cancelling your subscription",
    a: "Subscriptions are billed by the store, so they are managed on your device: on iOS, Settings → your name → Subscriptions; on Android, Play Store → Payments & subscriptions → Subscriptions. Cancelling there stops the next renewal and you keep access until the period you already paid for ends. The lifetime purchase does not renew, so there is nothing to cancel.",
  },
  {
    q: "Deleting your account",
    a: "You can erase your account and everything in it from inside the app, at any time, or from fazly.app/delete.",
  },
  {
    q: "Reporting a profile",
    a: "Any profile you see on a leaderboard can be reported to us at this address. We review reports and remove offending profiles and the accounts behind them, typically within 24 hours.",
  },
] as const;

export const Route = createFileRoute("/support")({
  headers: documentHeaders,
  head: () => ({
    meta: [
      { "script:ld+json": faqJsonLd([...SUPPORT_FAQ]) },
      { title: "Support — Fazly" },
      {
        name: "description",
        content:
          "Get help with Fazly: your scan, your photos, subscriptions and account deletion.",
      },
    ],
    links: [{ rel: "canonical", href: canonical("/support") }],
  }),
  component: Support,
});

function Support() {
  return (
    <PageChrome title="Support">
      <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink2">
        Something not working, or a question about your subscription? Write to
        us and a person will answer.
      </p>

      <div className="mt-8 flex flex-col items-start gap-3">
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="rim inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
        >
          Email {SUPPORT_EMAIL}
        </a>
        <span className="text-[13px] text-ink2">
          We answer in English and Turkish.
        </span>
      </div>

      <section className="mt-12">
        <h2 className="font-display text-[22px] font-bold text-ink">
          Common questions
        </h2>

        <div className="mt-5 max-w-2xl space-y-6 text-[16px] leading-relaxed text-ink2">
          <div>
            <h3 className="font-semibold text-ink">
              What happens to my photos
            </h3>
            <p className="mt-1.5">
              They are used for the analysis and deleted within seven days. They
              are sent only to the provider that scores them, they are never
              shown to another user, and they are never used to train anything.
              Deleting your profile removes them straight away.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-ink">
              My scan failed or the result looks wrong
            </h3>
            <p className="mt-1.5">
              Take it again in even, front-on light with your hair off your face
              and nothing covering your jaw. A score is an estimate from a
              handful of photographs, so two scans in different light can
              disagree — if one looks badly wrong, retake it and write to us if
              it happens again.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-ink">
              Managing or cancelling your subscription
            </h3>
            <p className="mt-1.5">
              Subscriptions are billed by the store, so they are managed on your
              device: on iOS, Settings &rarr; your name &rarr; Subscriptions; on
              Android, Play Store &rarr; Payments &amp; subscriptions &rarr;
              Subscriptions. Cancelling there stops the next renewal and you
              keep access until the period you already paid for ends. The
              lifetime purchase does not renew, so there is nothing to cancel.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-ink">Deleting your account</h3>
            <p className="mt-1.5">
              You can erase your account and everything in it from inside the
              app, at any time. See{" "}
              <a
                className="underline underline-offset-2 hover:opacity-70"
                href="/delete"
              >
                fazly.app/delete
              </a>
              .
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-ink">Reporting a profile</h3>
            <p className="mt-1.5">
              Any profile you see on a leaderboard can be reported to us at this
              address. We review reports and remove offending profiles and the
              accounts behind them, typically within 24 hours.
            </p>
          </div>
        </div>
      </section>

      <div className="brutal mt-12 max-w-2xl px-5 py-4 text-[15px] leading-relaxed text-ink2">
        Fazly is made by AKES Solutions Limited. See our{" "}
        <a
          className="underline underline-offset-2 hover:opacity-70"
          href="/privacy"
        >
          Privacy Policy
        </a>{" "}
        and{" "}
        <a
          className="underline underline-offset-2 hover:opacity-70"
          href="/terms"
        >
          Terms of Use
        </a>
        .
      </div>
    </PageChrome>
  );
}
